import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Crosshair, RefreshCw } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const probColor = (p) => p >= 50 ? 'text-brand-lime' : p >= 35 ? 'text-brand-cyan' : 'text-white';

const fmtTime = (utc) => {
  try {
    return new Date(utc).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET';
  } catch { return ''; }
};

const ScorerRow = ({ s, i, testPrefix }) => (
  <div className="flex items-center gap-3 py-2 border-b border-[#1e3a5f]/40 last:border-0" data-testid={`${testPrefix}-${i}`}>
    <span className="font-mono text-muted-foreground text-xs w-5">{i + 1}</span>
    <img
      src={s.headshot}
      alt={s.name}
      className="w-9 h-9 rounded-full bg-[#112240] object-cover"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
    <div className="flex-1 min-w-0">
      <div className="text-white font-medium text-sm truncate">{s.name}</div>
      <div className="text-[11px] text-muted-foreground">{s.team} · {s.pos} · {s.home ? 'Home' : 'Away'} · {s.l10_goals}G in {s.l10_gp} · {s.spg} SOG/g</div>
    </div>
    <div className="text-right">
      <div className={`font-mono font-bold text-lg ${probColor(s.prob)}`}>{s.prob}%</div>
      <div className="text-[9px] text-muted-foreground uppercase">Anytime Goal</div>
    </div>
  </div>
);

const GoalsZone = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/nhl/goals-zone`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const top = data?.top_scorers?.[0];

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Crosshair className="h-8 w-8 text-brand-lime" />
                Goals Zone
              </h1>
              <p className="text-muted-foreground mt-1">Top anytime goal scorer probabilities per game — L10 goal trends, shot volume, matchup defense & home/away splits</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-goals-zone-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-56 skeleton rounded-lg" />)}</div>
          ) : !data || !data.games?.length ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No NHL games on the slate right now — check back on a game day.</CardContent></Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="goals-zone-summary">
                {top && (
                  <Badge className="bg-brand-lime/10 text-brand-lime border border-brand-lime text-sm px-3 py-1">
                    Top goal chance: {top.prob}% — {top.name}
                  </Badge>
                )}
                <Badge variant="outline" className="border-brand-cyan text-brand-cyan text-sm px-3 py-1">
                  {data.games_count} games
                </Badge>
                <Badge variant="outline" className="border-[#1e3a5f] text-white text-sm px-3 py-1">
                  {data.date}
                </Badge>
              </div>

              <Tabs defaultValue="games">
                <TabsList className="bg-[#0f1e36] border border-[#1e3a5f] p-1 mb-6 flex-wrap h-auto">
                  {['games', 'players'].map(t => (
                    <TabsTrigger key={t} value={t} className="data-[state=active]:bg-brand-cyan data-[state=active]:text-[#0a1628] uppercase font-bold" data-testid={`goals-zone-tab-${t}`}>
                      {t}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="games">
                  <div className="grid lg:grid-cols-2 gap-4">
                    {data.games.map((g, gi) => (
                      <Card key={g.game_id || gi} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`goals-zone-game-${gi}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="font-heading text-lg font-bold text-white uppercase">{g.away_abbr} @ {g.home_abbr}</div>
                              <div className="text-xs text-muted-foreground">{g.away_name} at {g.home_name} • {g.state === 'FUT' ? fmtTime(g.start_utc) : g.state}</div>
                            </div>
                            <div className="text-right text-[10px] text-muted-foreground uppercase">
                              <div>GA/G: {g.away_abbr} {g.away_ga_pg ?? '—'}</div>
                              <div>GA/G: {g.home_abbr} {g.home_ga_pg ?? '—'}</div>
                            </div>
                          </div>
                          <div>
                            {(g.scorers || []).slice(0, 8).map((s, i) => (
                              <ScorerRow key={s.player_id} s={s} i={i} testPrefix={`gz-scorer-${gi}`} />
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="players">
                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardContent className="p-5">
                      <div className="text-sm text-muted-foreground mb-3">Highest anytime-goal probabilities across today's slate</div>
                      {(data.top_scorers || []).map((s, i) => (
                        <ScorerRow key={s.player_id} s={s} i={i} testPrefix="gz-top-scorer" />
                      ))}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default GoalsZone;
